var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock, Users, Flame, BookMarked, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatTime, formatIngredient } from "../../utils/recipeHelpers";
import { Separator } from "@/components/ui/separator";
import { isRecipeSaved, saveRecipe, removeRecipe } from "../../utils/recipeStorage";
import { toast } from "sonner";
import { useState, useEffect } from "react";
// Type guards
const isDetailedRecipe = (recipe) => {
    return 'get_set' in recipe || 'safety_notes' in recipe;
};
const isLegacyRecipe = (recipe) => {
    return 'ingredients' in recipe && !('ingredients_1f' in recipe);
};
const isSideRecipe = (recipe) => {
    return 'baseServings' in recipe && !('variations' in recipe);
};
export function QuickviewModal({ open, onOpenChange, recipe, onViewFullRecipe, }) {
    const [isInRecipeBox, setIsInRecipeBox] = useState(false);
    useEffect(() => {
        if (!recipe)
            return;
        const checkRecipeBox = () => __awaiter(this, void 0, void 0, function* () {
            const recipeId = isDetailedRecipe(recipe) ? recipe.recipe_id : recipe.id;
            const inBox = yield isRecipeSaved(recipeId);
            setIsInRecipeBox(inBox);
        });
        checkRecipeBox();
    }, [recipe, open]);
    if (!recipe)
        return null;
    // Extract common properties based on recipe type
    const getRecipeInfo = () => {
        if (isDetailedRecipe(recipe)) {
            return {
                id: recipe.recipe_id,
                name: recipe.title,
                summary: recipe.summary,
                image: "", // DetailedRecipe doesn't have direct image
                totalTime: recipe.cook_time_total_minutes + recipe.prep_time_minutes,
                servings: recipe.yield_base_servings,
                flameLevel: recipe.flame,
                ingredients: recipe.ingredients_1f || [],
                steps: recipe.steps_1f || [],
            };
        }
        else if (isLegacyRecipe(recipe)) {
            return {
                id: recipe.id,
                name: recipe.name,
                summary: recipe.summary,
                image: recipe.image,
                totalTime: recipe.cookTime + recipe.prepTime,
                servings: recipe.servings,
                flameLevel: recipe.flameLevel,
                ingredients: recipe.ingredients,
                steps: recipe.steps,
            };
        }
        else if (isSideRecipe(recipe)) {
            return {
                id: recipe.id,
                name: recipe.name,
                summary: recipe.summary,
                image: recipe.image,
                totalTime: recipe.cookTime + recipe.prepTime,
                servings: recipe.servings,
                flameLevel: recipe.flameLevel,
                ingredients: recipe.ingredients,
                steps: recipe.steps,
            };
        }
        return null;
    };
    const info = getRecipeInfo();
    if (!info)
        return null;
    const handleAddToRecipeBox = () => {
        // Get recipe identifier - handle both DetailedRecipe and legacy Recipe
        const recipeId = isDetailedRecipe(recipe) ? recipe.recipe_id : info.id;
        const recipeSlug = isDetailedRecipe(recipe) ? recipe.slug : recipe.slug;
        if (isInRecipeBox) {
            removeRecipe(recipeSlug || recipeId);
            setIsInRecipeBox(false);
            toast.success("Removed from Recipe Box");
        }
        else {
            const savedRecipe = {
                id: recipeId,
                slug: recipeSlug,
                recipe_id: recipeId,
                title: info.name,
                description: info.summary,
                image: info.image,
                cookTime: `${info.totalTime} min`,
                servings: info.servings,
                difficulty: info.flameLevel,
                category: "Main Dish",
                savedAt: new Date().toISOString(),
            };
            saveRecipe(savedRecipe);
            setIsInRecipeBox(true);
            toast.success("Added to Recipe Box");
        }
    };
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogContent, { className: "max-w-3xl max-h-[85vh] overflow-y-auto", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: info.name }), _jsx(DialogDescription, { children: info.summary })] }), _jsxs("div", { className: "space-y-6", children: [info.image && (_jsx("img", { src: info.image, alt: info.name, className: "w-full h-48 object-cover rounded-lg" })), _jsxs("div", { className: "flex flex-wrap items-center gap-6 text-sm text-[#5a4a3a]", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Clock, { className: "w-4 h-4" }), _jsx("span", { children: formatTime(info.totalTime) })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Users, { className: "w-4 h-4" }), _jsxs("span", { children: [info.servings, " servings"] })] }), _jsx("div", { className: "flex items-center gap-2", children: _jsx("div", { className: "flex gap-0.5", children: [1, 2, 3].map((level) => (_jsx(Flame, { className: `w-4 h-4 ${level <= info.flameLevel
                                                ? "fill-[#c85a3f] text-[#c85a3f]"
                                                : "fill-[#d4d4d4] text-[#d4d4d4]"}` }, level))) }) })] }), _jsx(Separator, {}), _jsxs("div", { children: [_jsx("h3", { className: "mb-3 text-[#5a4a3a]", children: "Ingredients" }), _jsxs("ul", { className: "space-y-2 text-[#5a4a3a]", children: [info.ingredients.slice(0, 8).map((ingredient, index) => (_jsxs("li", { className: "flex items-start gap-2", children: [_jsx("span", { className: "text-[#c85a3f] mt-1", children: "\u2022" }), _jsx("span", { children: isDetailedRecipe(recipe)
                                                        ? `${ingredient.amount || ""} ${ingredient.unit || ""} ${ingredient.item}`.trim()
                                                        : formatIngredient(ingredient, false) })] }, index))), info.ingredients.length > 8 && (_jsxs("li", { className: "text-sm text-[#5a4a3a]/60 italic", children: ["+ ", info.ingredients.length - 8, " more ingredients..."] }))] })] }), _jsxs("div", { children: [_jsx("h3", { className: "mb-3 text-[#5a4a3a]", children: "Instructions Preview" }), _jsxs("ol", { className: "space-y-3", children: [info.steps.slice(0, 3).map((step, index) => (_jsxs("li", { className: "flex gap-3 text-[#5a4a3a]", children: [_jsx("span", { className: "flex-shrink-0 w-6 h-6 rounded-full bg-[#c85a3f] text-white flex items-center justify-center text-sm", children: index + 1 }), _jsx("span", { className: "flex-1", children: isDetailedRecipe(recipe) ? step.instruction : step.instruction })] }, index))), info.steps.length > 3 && (_jsxs("li", { className: "text-sm text-[#5a4a3a]/60 italic pl-9", children: ["+ ", info.steps.length - 3, " more steps..."] }))] })] }), _jsxs("div", { className: "pt-4 border-t border-[#e8e3db] space-y-3", children: [onViewFullRecipe && (_jsxs(Button, { onClick: () => {
                                        onViewFullRecipe();
                                        onOpenChange(false);
                                    }, className: "w-full bg-[#c85a3f] hover:bg-[#b34e39] text-white", children: ["View Full Recipe", _jsx(ArrowRight, { className: "w-4 h-4 ml-2" })] })), _jsxs(Button, { onClick: handleAddToRecipeBox, variant: "outline", className: `w-full ${isInRecipeBox
                                        ? "border-[#a8c5a4] text-[#a8c5a4] hover:bg-[#a8c5a4]/10"
                                        : "border-[#c85a3f] text-[#c85a3f] hover:bg-[#c85a3f]/10"}`, children: [_jsx(BookMarked, { className: "w-4 h-4 mr-2" }), isInRecipeBox ? "Remove from Recipe Box" : "Add to Recipe Box"] }), _jsx("p", { className: "text-sm text-[#5a4a3a]/60 text-center", children: isInRecipeBox
                                        ? "This recipe is saved in your Recipe Box"
                                        : "Save this recipe to easily find it later" })] })] })] }) }));
}
